import { _ } from "../../../../core/polyglot/polyglot.js";
import { select, theme } from "../../../../core/elements/index.js";
import AccountGroupRowExclusionWidget from "../../../../core/widgets/AccountGroupRowExclusionWidget.js";
import accountOperationFactory from "../../../../models/keyFigure/accountOperations/accountOperationFactory.js";
import ButtonGroupWidget from "../../../../core/widgets/ButtonGroupWidget/ButtonGroupWidget.js";
import CountWidget from "../../../../core/widgets/CountWidget/CountWidget.js";
import dialog from "../../../../core/widgets/dialog.js";
import ExclusionGroupSet from "../../../../models/reports/reports/ExclusionGroupSet.js";
import FixedPeriodTemplate from "../../../../models/reports/templates/FixedPeriodTemplate.js";
import FormBuilder from "../../../../core/widgets/forms/FormBuilder.js";
import FormulaEditorWidget from "../../FormulaEditorWidget.js";
import IndexPicker from "../../../shared/IndexPicker/IndexPicker.js";
import multipleDimensionsPickerWidget from "../../../../core/widgets/multipleDimensionsPickerWidget.js";
import popoverActionButtonWidget from "../../../../core/widgets/popoverActionButtonWidget.js";
import RelativePeriodTemplate from "../../../../models/reports/templates/RelativePeriodTemplate.js";
import ReportTemplateVisitor from "../../../../models/reports/templates/ReportTemplateVisitor.js";
import session from "../../../../core/session.js";
import SwitchButton from "../../../../core/widgets/SwitchButton/SwitchButton.js";
import TranslatedString from "../../../../models/TranslatedString.js";
import VisibilitySettingsWidget from "./VisibilitySettingsWidget/VisibilitySettingsWidget.js";

/**
 * Visitor responsible for rendering right menu actions for the selected
 * report template item.
 *
 * @param {dimension} spec.dimensionTree - Dimension tree used for dimension item
 * templates.
 * @param {chartOfAccounts} spec.chartOfAccounts -
 */
export default class DocumentActionsRenderer extends ReportTemplateVisitor {
	constructor({ dimensionTree, chartOfAccounts } = {}) {
		super(...arguments);
		this._dimensionTree = dimensionTree;
		this._chartOfAccounts = chartOfAccounts;
	}

	itemActionsRenderer(itemTemplate) {
		return (document) =>
			this._rightMenuSectionRenderer(
				itemTemplate,
				this.visit(itemTemplate).map((renderer) => renderer(document)),
			);
	}

	visitAbstractRowTemplate(row) {
		return [
			this._visibilityOptionsRenderer({ item: row }),
			this._editItemRenderer(row),
			this._deleteItemRenderer(row),
		];
	}

	visitAbstractValueRowTemplate(row) {
		if (row.isTopLevel()) {
			return [
				this._dimensionSwitchRenderer(row, _("Dimension row")),
				this._accountOperationDropdownRenderer(row),
				...super.visitAbstractValueRowTemplate(row),
			];
		}

		return [
			this._dimensionSwitchRenderer(row, _("Dimension row")),
			...super.visitAbstractValueRowTemplate(row),
		];
	}

	visitRepeatingRowTemplate(row) {
		return [
			this._dimensionsPickerRenderer(
				row,
				() => row.getDimensionSpecification(),
				(value) => row.setDimensionIdsFromSpecification(value),
			),
			this._visibilityOptionsRenderer({ item: row }),
			this._deleteItemRenderer(row),
		];
	}

	visitAbstractAccountRowTemplate(row) {
		if (row.isTopLevel()) {
			return [
				this._creditPositiveSwitchRenderer(row),
				...super.visitAbstractAccountRowTemplate(row),
			];
		}

		return super.visitAbstractAccountRowTemplate(row);
	}

	visitSpacerRowTemplate(row) {
		return [
			this._horizontalLineSwitchRenderer(row),
			this._heightSwitchRenderer(row),
			this._deleteItemRenderer(row),
			this._visibilityOptionsRenderer({
				item: row,
				visibilityOptions: ["always-visible", "master-visibility"],
			}),
		];
	}

	visitTextRowTemplate(row) {
		return [
			this._rowStylesDropdownRenderer(row),
			...super.visitTextRowTemplate(row),
		];
	}

	visitFormulaRowTemplate(row) {
		return [
			this._rowStylesDropdownRenderer(row),
			this._rowFormulaEditorRenderer(row),
			this._percentageSwitchRenderer(row),
			...super.visitFormulaRowTemplate(row),
		];
	}

	visitKeyFigureRowTemplate(row) {
		return [
			this._rowStylesDropdownRenderer(row),
			this._invertSignSwitchRenderer(row),
			this._visibilityOptionsRenderer({ item: row }),
			this._editItemRenderer(row),
			this._deleteItemRenderer(row),
		];
	}

	visitAccountGroupRowTemplate(row) {
		return [
			this._accountGroupRowExclusionRenderer(row),
			...super.visitAccountGroupRowTemplate(row),
		];
	}

	visitAbstractColumnTemplate(column) {
		const visibilityOptions = [
			"always-visible",
			"visible-if-data",
			"always-hidden",
			"optional-visible",
			"optional-hidden",
		];

		return [
			this._visibilityOptionsRenderer({
				item: column,
				visibilityOptions,
			}),
			this._customTitleRenderer(column),
			this._deleteItemRenderer(column),
		];
	}

	visitAbstractPeriodColumnTemplate(column) {
		return [
			this._optionalSwitchRenderer(column),
			this._budgetSwitchRenderer(column),
			this._dimensionSwitchRenderer(column, _("Dimension column")),
			...super.visitAbstractPeriodColumnTemplate(column),
			this._periodTypeSwitchRenderer(column),
			column.getPeriodTemplate().documentActionsRenderer(column),
		];
	}

	visitRollingPeriodColumnTemplate(column) {
		return [
			this._rollingPeriodsPickerRenderer(column),
			...super.visitRollingPeriodColumnTemplate(column),
		];
	}

	visitDeviationColumnTemplate(column) {
		return [
			this._deviationSettingsRenderer(column),
			this._optionalSwitchRenderer(column),
			...super.visitDeviationColumnTemplate(column),
		];
	}

	visitFormulaColumnTemplate(column) {
		return [
			this._columnFormulaEditorRenderer(column),
			this._optionalSwitchRenderer(column),
			this._percentageSwitchRenderer(column),
			...super.visitFormulaColumnTemplate(column),
		];
	}

	visitSeparatorColumnTemplate(column) {
		return [
			this._fullWidthSwitchRenderer(column),
			this._deleteItemRenderer(column),
		];
	}

	visitPercentageColumnTemplate(column) {
		return [
			this._optionalSwitchRenderer(column),
			this._makeAbsoluteSwitchRenderer(column),
			this._percentageColumnSettingsRenderer(column),
			...super.visitPercentageColumnTemplate(column),
		];
	}

	_rightMenuSectionRenderer(itemTemplate, contents) {
		return (html) => {
			html.div(
				{ klass: "right-menu-section" },
				html.label(
					_("{item} settings", { item: itemTemplate.getLabel() }),
				),
				contents,
			);
		};
	}

	_editItemRenderer(item) {
		return (document) => (html) => {
			html.a(
				{
					klass: "btn btn-default btn-block",
					"data-test": "edit-item",
				},
				item.isRow() ? _("Edit row") : _("Edit column"),
			).click(() => document.editItem(item));
		};
	}

	_deleteItemRenderer(item) {
		return (document) => (html) => {
			html.a(
				{
					klass: "btn btn-default btn-block",
					"data-test": "delete-item",
				},
				item.isRow() ? _("Delete row") : _("Delete column"),
			).click(() => document.removeItem(item));
		};
	}

	_visibilityOptionsRenderer({ item, visibilityOptions }) {
		return (document) => (html) => {
			if (!item.getParent().isVisible()) {
				return;
			}

			const visibilitySettings = new VisibilitySettingsWidget({
				item,
				report: document.getReport(),
				visibilityOptions,
			});

			const popoverButton = popoverActionButtonWidget({
				label: _("Edit visibility"),
				dataTestName: "edit-visibility",
				popoverHeader: _("Visibility settings"),
				popoverAction: () => {},
				popoverContent: (html) => {
					visibilitySettings.reset();
					html.render(visibilitySettings);

					popoverButton.popoverAction.registerOnce(() => {
						visibilitySettings.updateVisibility();
						document.updateItem(item);
					});
				},
			});

			html.render(popoverButton);
		};
	}

	_percentageSwitchRenderer(itemTemplate) {
		return (document) =>
			this._switchRenderer({
				state: itemTemplate.getFormatInPercentage(),
				action: (state) => {
					itemTemplate.setFormatInPercentage(state);
					document.updateItem(itemTemplate);
				},
				label: _("Percentage"),
			});
	}

	_budgetSwitchRenderer(columnTemplate) {
		return (document) =>
			this._switchRenderer({
				state: columnTemplate.isBudgetColumn(),
				action: (state) => {
					columnTemplate.setBudgetColumn(state);
					document.updateItem(columnTemplate);
				},
				label: _("Budget column"),
			});
	}

	_periodTypeSwitchRenderer(columnTemplate) {
		return (document) => {
			if (!columnTemplate.canEditPeriodTemplateType()) {
				return () => {};
			}

			return this._switchRenderer({
				state: columnTemplate.hasFixedPeriodTemplate(),
				action: (state) => {
					if (state) {
						columnTemplate.setPeriodTemplate(
							new FixedPeriodTemplate({
								period: session.getPeriod(),
							}),
						);
					} else {
						columnTemplate.setPeriodTemplate(
							new RelativePeriodTemplate(),
						);
					}

					document.updateItem(columnTemplate);
				},
				label: _("Calendar period"),
			});
		};
	}

	_fullWidthSwitchRenderer(columnTemplate) {
		return (document) => {
			const button = new ButtonGroupWidget({
				states: [
					{
						label: _("Narrow"),
						value: true,
					},
					{
						label: _("Wide"),
						value: false,
					},
				],
			});

			button.setStateByValue(columnTemplate.isNarrow());

			button.stateChanged.register((state) => {
				columnTemplate.setNarrow(state.value);
				document.updateItem(columnTemplate);
			});

			return (html) => {
				html.div({ klass: "right-menu-block" }, button);
			};
		};
	}

	_heightSwitchRenderer(rowTemplate) {
		return (document) => {
			const button = new ButtonGroupWidget({
				states: [
					{
						label: _("Normal"),
						value: true,
					},
					{
						label: _("Large"),
						value: false,
					},
				],
			});

			button.setStateByValue(rowTemplate.isShort());

			button.stateChanged.register((state) => {
				rowTemplate.setShort(state.value);
				document.updateItem(rowTemplate);
			});

			return (html) => {
				html.div({ klass: "right-menu-block" }, button);
			};
		};
	}

	_dimensionSwitchRenderer(itemTemplate, label) {
		return (document) => (html) => {
			html.render(
				this._switchRenderer({
					state: itemTemplate.hasOwnDimensionSpecification(),
					action: (state) => {
						itemTemplate.setHasDimensionSpecification(state);
						document.updateItem({ keepExpansion: true });
					},
					label,
					dataTestName: "switch-dimension-row",
				}),
			);

			if (itemTemplate.hasOwnDimensionSpecification()) {
				html.render(
					this._defaultDimensionsPickerRenderer(itemTemplate)(
						document,
					),
				);
			}
		};
	}

	_invertSignSwitchRenderer(rowTemplate) {
		return (document) =>
			this._switchRenderer({
				state: rowTemplate.isNegated(),
				action: (state) => {
					rowTemplate.setNegated(state);
					document.updateItem(rowTemplate);
				},
				label: _("Invert sign"),
			});
	}

	_creditPositiveSwitchRenderer(rowTemplate) {
		return (document) =>
			this._switchRenderer({
				state: rowTemplate.isCreditPositive(),
				action: (state) => {
					rowTemplate.setCreditPositive(state);
					document.updateItem(rowTemplate);
				},
				label: _("Credit positive"),
			});
	}

	_optionalSwitchRenderer(columnTemplate) {
		return (document) =>
			this._switchRenderer({
				state: columnTemplate.isOptional(),
				action: (state) => {
					columnTemplate.setOptional(state);
					document.updateItem(columnTemplate);
				},
				label: _("Optional"),
			});
	}

	_makeAbsoluteSwitchRenderer(columnTemplate) {
		return (document) =>
			this._switchRenderer({
				state: columnTemplate.shouldMakeAbsolute(),
				action: (state) => {
					columnTemplate.setMakeAbsolute(state);
					document.updateItem(columnTemplate);
				},
				label: _("Show +/-"),
			});
	}

	_customTitleRenderer(columnTemplate) {
		return (document) => {
			const form = new FormBuilder({
				model: columnTemplate,
				showActions: false,
			});

			form.input({
				label: _("Custom title"),
				placeHolder: _("Column title"),
				help: _("Leave empty to use the default title"),
				accessor: () => columnTemplate.getCustomTitle(),
				mutator: (value) => {
					let newCustomTitle;

					if (value.length !== 0) {
						newCustomTitle = new TranslatedString({ value });
					}

					columnTemplate.setCustomTitle(newCustomTitle);
				},
			});

			const formDialog = dialog({
				header: _("Custom title"),
				body: form,
				footer: (html) => {
					html.button({ klass: "btn btn-primary" }, _("Save")).click(
						() => {
							form.submit();
							if (!form.invalidFields().length) {
								formDialog.hide();
								document.updateItem(columnTemplate);
							}
						},
					);

					html.button(
						{ klass: "btn btn-default" },
						_("Cancel"),
					).click(() => formDialog.hide());
				},
			});

			return (html) => {
				html.a(
					{ klass: "btn btn-default btn-block" },
					_("Edit custom title"),
				).click(() => formDialog.show());
			};
		};
	}

	_rollingPeriodsPickerRenderer(columnTemplate) {
		return (document) => {
			const dropdown = select({
				class: "inline-dropdown",
				items: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 24, 36, 48, 60].map(
					(item) => ({ label: item, value: item }),
				),
				selected: columnTemplate.getNumberOfMonths(),
				onChange: (numberOfMonths) => {
					columnTemplate.setNumberOfMonths(numberOfMonths);
					document.updateItem(columnTemplate);
				},
			});

			return (html) => {
				html.div(
					{ klass: "right-menu-block" },
					html.span(
						{ klass: "inline-dropdown-label" },
						_("Rolling months"),
					),
					theme({ name: "dark-select" }, dropdown),
				);
			};
		};
	}

	_horizontalLineSwitchRenderer(rowTemplate) {
		return (document) =>
			this._switchRenderer({
				state: rowTemplate.isHorizontalLine(),
				action: (state) => {
					rowTemplate.setHorizontalLine(state);
					document.updateItem(rowTemplate);
				},
				label: _("Horizontal line"),
			});
	}

	_switchRenderer({ state, action, label, dataTestName }) {
		return (html) => {
			const button = new SwitchButton({
				state,
				dataTestName,
			});

			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			button.stateChanged.register((state) => {
				action(state);
			});

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, label),
				button,
			);
		};
	}

	_rowFormulaEditorRenderer(rowTemplate) {
		return this._formulaEditorRenderer({
			itemTemplate: rowTemplate,
			canAddItemToFormula: (item) =>
				item.isRow() && item.canBeUsedInFormulas(),
		});
	}

	_columnFormulaEditorRenderer(columnTemplate) {
		return this._formulaEditorRenderer({
			itemTemplate: columnTemplate,
			canAddItemToFormula: (item) =>
				item.isColumn() && item.canBeUsedInFormulas(),
		});
	}

	_formulaEditorRenderer({ itemTemplate, canAddItemToFormula }) {
		return (document) => (html) => {
			const formulaEditor = new FormulaEditorWidget({
				formula: itemTemplate.getFormula(),
				report: document.getReport(),
				dimensionTree: this._dimensionTree,
				canAddItemToFormula,
			});

			formulaEditor.saved.register((formula) => {
				itemTemplate.setFormula(formula);
				document.updateItem(itemTemplate);
			});

			formulaEditor.cancelled.register(() =>
				document.updateItem(itemTemplate),
			);

			html.div({ klass: "right-menu-block" }, formulaEditor);
		};
	}

	_defaultDimensionsPickerRenderer(itemTemplate) {
		return (document) =>
			this._dimensionsPickerRenderer(
				itemTemplate,
				() => itemTemplate.getDefaultDimensionSpecification(),
				(value) => itemTemplate.setDefaultDimensionSpecification(value),
			)(document);
	}

	_dimensionsPickerRenderer(itemTemplate, getter, setter) {
		return (document) => (html) => {
			const dimensionPicker = multipleDimensionsPickerWidget({
				dimensionTree: this._dimensionTree,
				dimensionSpecification: getter(),
			});

			dimensionPicker.onDimensionsSelected.register(
				(dimensionSpecification) => {
					setter(dimensionSpecification);
					document.updateItem(itemTemplate);
				},
			);

			html.div({ klass: "right-menu-block" }, dimensionPicker);
		};
	}

	_deviationSettingsRenderer(columnTemplate) {
		return (document) => (html) => {
			const baseValuesDropdown = this._makeColumnDropdown({
				document,
				columnTemplate,
				accessor: () => columnTemplate.getBaseValuesColumnTemplate(),
				callback: (selectedColumnTemplate) => {
					columnTemplate.setBaseValuesColumnTemplate(
						selectedColumnTemplate,
					);

					document.updateItem(columnTemplate);
				},
			});

			const deviatedValuesDropdown = this._makeColumnDropdown({
				document,
				columnTemplate,
				accessor: () =>
					columnTemplate.getDeviatedValuesColumnTemplate(),
				callback: (selectedColumnTemplate) => {
					columnTemplate.setDeviatedValuesColumnTemplate(
						selectedColumnTemplate,
					);

					document.updateItem(columnTemplate);
				},
			});

			const inPercentSwitch = new ButtonGroupWidget({
				states: [
					{
						label: _("Amount"),
						value: false,
						active: !columnTemplate.hasPercentageFormat(),
					},
					{
						label: _("Percent"),
						value: true,
						active: columnTemplate.hasPercentageFormat(),
					},
				],
			});

			inPercentSwitch.stateChanged.register((newState) => {
				columnTemplate.setInPercent(newState.value);
				document.updateItem(columnTemplate);
			});

			html.div(
				{ klass: "right-menu-block" },
				_("Base values"),
				baseValuesDropdown,
				_("Deviated values"),
				deviatedValuesDropdown,
			);

			html.div({ klass: "right-menu-block" }, inPercentSwitch);
		};
	}

	_percentageColumnSettingsRenderer(columnTemplate) {
		return (document) => (html) => {
			const rowDropdown = this._makeRowDropdown({
				document,
				accessor: () => columnTemplate.getRowTemplate(),
				callback: (selectedRowTemplate) => {
					columnTemplate.setRowTemplate(selectedRowTemplate);
					document.updateItem(columnTemplate);
				},
			});

			const columnDropdown = this._makeColumnDropdown({
				document,
				columnTemplate,
				accessor: () => columnTemplate.getColumnTemplate(),
				callback: (selectedColumnTemplate) => {
					columnTemplate.setColumnTemplate(selectedColumnTemplate);
					document.updateItem(columnTemplate);
				},
			});

			html.div(
				{ klass: "right-menu-block" },
				_("Row"),
				rowDropdown,
				_("Column"),
				columnDropdown,
			);
		};
	}

	_accountOperationDropdownRenderer(rowTemplate) {
		return (document) => (html) => {
			const defaultOperation = {
				value: { operation: null },
				label: _("Same as Column"),
			};

			const accountOperations = accountOperationFactory
				.allInstances()
				.map((operation) => ({
					value: { operation },
					label: operation.getName(),
				}));

			const selectedItem =
				this._findRowOperation({ rowTemplate, accountOperations }) ||
				defaultOperation;

			const dropdown = select({
				items: [defaultOperation, ...accountOperations],
				selected: selectedItem.value,
				onChange: (item) => {
					rowTemplate.setAccountOperation(item.operation);
					document.updateItem(rowTemplate);
				},
			});

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, _("Calculate as...")),
				theme({ name: "dark-select" }, dropdown),
			);
		};
	}

	_findRowOperation({ rowTemplate, accountOperations }) {
		const rowAccountOperation = rowTemplate.getAccountOperation();
		if (!rowAccountOperation) return undefined;

		return accountOperations.find((each) =>
			rowAccountOperation.equals(each.value.operation),
		);
	}

	_makeColumnDropdown({ document, columnTemplate, accessor, callback }) {
		const report = document.getReport();
		const columns = report
			.getColumns()
			.filter(
				(col) => col.getTemplate().getId() !== columnTemplate.getId(),
			);

		const selectedColumn = columns.find(
			(column) => column.getTemplate() === accessor(),
		);

		return theme(
			{ name: "dark-select" },
			select({
				items: columns.map((column) => ({
					label: column.getFullTitleString(),
					value: column,
				})),
				selected: selectedColumn,
				allowEmptySelection: true,
				onChange: (column) => callback(column.getTemplate()),
			}),
		);
	}

	_makeRowDropdown({ document, accessor, callback }) {
		const report = document.getReport();
		const rows = report.getRows().filter((row) => row.canBeReferenced());

		const selectedRow = rows.find(
			(row) => row.getTemplate() === accessor(),
		);

		return theme(
			{ name: "dark-select" },
			select({
				items: rows.map((row) => ({
					label: row.getTitle(),
					value: row,
				})),
				selected: selectedRow,
				allowEmptySelection: true,
				onChange: (row) => callback(row.getTemplate()),
			}),
		);
	}

	_rowStylesDropdownRenderer(rowTemplate) {
		const styles = [
			{
				label: (html) => {
					html.span({ klass: "report-text-row-text" }, _("Text"));
				},
				value: "text",
			},
			{
				label: (html) => {
					html.span(
						{ klass: "report-text-row-heading" },
						_("Heading"),
					);
				},
				value: "heading",
			},
			{
				label: (html) => {
					html.span(
						{ klass: "report-text-row-subheading" },
						_("Sub heading"),
					);
				},
				value: "subheading",
			},
			{
				label: (html) => {
					html.span(
						{ klass: "report-text-row-highlight" },
						_("Highlight"),
					);
				},
				value: "highlight",
			},
		];

		return (document) => (html) => {
			const dropdown = select({
				items: styles,
				selected: rowTemplate.getStyle(),
				onChange: (value) => {
					rowTemplate.setStyle(value);
					document.updateItem(rowTemplate);
				},
			});

			html.div(
				{ klass: "right-menu-block" },
				html.label(_("Style")),
				theme({ name: "dark-select" }, dropdown),
			);
		};
	}

	_accountGroupRowExclusionRenderer(rowTemplate) {
		return (document) => (html) => {
			const exclusionGroupSet = new ExclusionGroupSet({
				rowTemplate,
				chartOfAccounts: this._chartOfAccounts,
			});

			const popover = new AccountGroupRowExclusionWidget({
				exclusionGroupSet,
			});

			const accountGroupRowExclusion = popoverActionButtonWidget({
				label: _("Change excluded groups"),
				popoverHeader: _("Account group exclusions"),
				popoverContent: popover,
				popoverAction: () => {
					exclusionGroupSet.updateExludedGroups();
					document.updateItem(rowTemplate);
				},
				dataTestName: "change-excluded-groups",
			});

			html.div(
				{ klass: "right-menu-block" },
				new CountWidget({
					elements: rowTemplate.getExcludedGroupsIds(),
					label: _("excluded group"),
					pluralLabel: _("excluded groups"),
				}),
				accountGroupRowExclusion,
			);
		};
	}
}

const makePeriodTypeDropdown = function (periodTypes, accessor, callback) {
	const items = periodTypes.map((periodType) => ({
		label: periodType,
		value: periodType.rawValue(),
	}));

	return theme(
		{ name: "dark-select" },
		select({
			items,
			selected: accessor(),
			onChange: callback,
		}),
	);
};

RelativePeriodTemplate.prototype.documentActionsRenderer = function (
	columnTemplate,
) {
	return (document) => (html) => {
		const periodTypeDropdown = this.makePeriodTypeDropdown({
			accessor: this.getPeriodType.bind(this),
			callback: (value) => {
				this.setPeriodType(value);
				document.updateItem(columnTemplate);
			},
		});

		const shiftPeriodTypeDropdown = this.makePeriodTypeDropdown({
			accessor: this.getShiftPeriodType.bind(this),
			callback: (value) => {
				this.setShiftPeriodType(value);
				document.updateItem(columnTemplate);
			},
		});

		const shiftPeriodPicker = new IndexPicker({
			index: this.getShift(),
			callback: (index) => {
				this.setShift(index);
				document.updateItem(columnTemplate);
			},
		});

		html.div({ klass: "right-section-block" }, (html) => {
			if (columnTemplate.canEditPeriodTemplateType()) {
				html.label({ class: "switch-label" }, _("Period type"));
				html.render(periodTypeDropdown);
			}

			html.label({ class: "switch-label" }, _("Shift period"));

			html.div(
				{
					klass: "shift-period-picker",
					"data-test": "shift-period-picker",
				},
				shiftPeriodPicker,
				shiftPeriodTypeDropdown,
			);
		});
	};
};

RelativePeriodTemplate.prototype.makePeriodTypeDropdown = function ({
	accessor,
	callback,
}) {
	return makePeriodTypeDropdown(
		[_("Current"), _("Month"), _("Quarter"), _("Year")],
		accessor,
		callback,
	);
};

FixedPeriodTemplate.prototype.documentActionsRenderer = function () {
	return (document) => (html) => {
		const periodTypeDropdown = this.makePeriodTypeDropdown((value) => {
			this.setPeriodType(value);
			document.updateItem(this);
		});

		const periodIndexPicker = this._makePeriodIndexPicker((index) => {
			this.setPeriodIndex(index);
			document.updateItem(this);
		});

		html.div({ klass: "right-section-block" }, [
			(html) => html.label({ klass: "subsection" }, _("Period")),
			periodTypeDropdown,
			periodIndexPicker,
		]);
	};
};

FixedPeriodTemplate.prototype.makePeriodTypeDropdown = function (callback) {
	return makePeriodTypeDropdown(
		[_("Month"), _("Quarter")],
		() => this._periodType,
		callback,
	);
};

FixedPeriodTemplate.prototype._makePeriodIndexPicker = function (callback) {
	return (html) => {
		html.div(
			{ klass: "right-section-block" },
			html.label(_("Shift period")),
			new IndexPicker({
				index: this._periodIndex,
				callback,
			}),
		);
	};
};
